import CustomDropdown from '../../reusable/dropdown';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { EditMemberParams } from '../../../params/redux/team';
import { editMemberSchema } from '../../../schema/team_schema';
import { editMemberThunk } from '../../../redux/thunks/team';
import LabelWrapper from '../../wrapper/label';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: AddPipelineModal
 * @paragraph: A modal component for adding a new pipeline. Uses Formik for form handling
 * and integrates with Redux for pipeline creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const EditMemberModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {PipelineParams} -  Initial form values for the pipeline creation form.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    // Initial form values for the pipeline creation form
    const { loaders, selectedTeamMember } = useSelector((state: RootState) => state.team);
    const initialValues: EditMemberParams = {
        user_id: selectedTeamMember?.id || 0,
        email: selectedTeamMember?.email || '',
        first_name: selectedTeamMember?.first_name || '',
        last_name: selectedTeamMember?.last_name || '',
        phone_number: selectedTeamMember?.phone_number || '',
        role: (selectedTeamMember?.role as 'organizationAdmin' | 'salesManager' | 'salesMember' | 'companyManager') || 'companyManager'
    };
    const dispatch = useDispatch<AppDispatch>();

    /**
     * @heading3: Functions
     * @heading4: Handles the form submission by dispatching a Redux thunk.
     * @param: {PipelineParams} values - The form values for creating a pipeline.
     */
    const handleSubmit = async (values: EditMemberParams) => {
        try {
            await dispatch(editMemberThunk(values)).unwrap();
            // Close the modal on successful submission
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    return <CustomModal open={open} handleClose={loaders.isUpdating ? () => null : onClose} title="Account Details">
        <Formik
            initialValues={initialValues}
            validationSchema={editMemberSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Input field for pipeline name */}
                        <Box display="flex" gap={2}>
                            <CustomInput
                                value={values.first_name}
                                onChange={handleChange('first_name')}
                                onBlur={handleBlur('first_name')}
                                error={touched.first_name && errors.first_name}
                                fullWidth
                                label="First Name"
                                placeholder="Enter value"
                                variant="outlined"
                                disabled={loaders.isUpdating}
                                mandatoryFields={true}
                            />
                            <CustomInput
                                value={values.last_name}
                                onChange={handleChange('last_name')}
                                onBlur={handleBlur('last_name')}
                                error={touched.last_name && errors.last_name}
                                fullWidth
                                label="Last Name"
                                placeholder="Enter value"
                                variant="outlined"
                                disabled={loaders.isUpdating}
                                mandatoryFields={true}
                            />
                        </Box>
                        <CustomInput
                            value={values.email}
                            onChange={handleChange('email')}
                            onBlur={handleBlur('email')}
                            error={touched.email && errors.email}
                            fullWidth
                            label="Email"
                            placeholder="Enter value"
                            variant="outlined"
                            disabled={true}
                            mandatoryFields={true}
                        />
                        <CustomInput
                            type="phone"
                            value={values.phone_number}
                            onChangeForPhone={handleChange('phone_number')}
                            onBlur={handleBlur('phone_number')}
                            error={touched.phone_number && errors.phone_number}
                            fullWidth
                            label="Phone Number"
                            placeholder="201-555-0123"
                            variant="outlined"
                            mandatoryFields={true}
                        />
                        <Box>
                            <LabelWrapper label="Team Role" isMandatory={true} />
                            <CustomDropdown
                                options={[
                                    { label: 'Sales Member', value: 'salesMember' },
                                    { label: 'Organization Admin', value: 'organizationAdmin' },
                                    { label: 'Sales Manager', value: 'salesManager' },
                                    { label: 'Company Manager', value: 'companyManager' }
                                ]}
                                value={values.role}
                                onChange={(e) => setFieldValue('role', e.target.value)}
                                disabled={loaders.isUpdating}
                            />
                        </Box>
                        {/* Action buttons for form submission or cancellation */}
                        <Box display="flex" gap={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isUpdating}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loaders.isUpdating}>
                                {loaders.isUpdating ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default EditMemberModal;